/**
 * Sync utilities for periodic background sync and notifications
 * Handles low-frequency fallback full sync and update notifications
 */

const SYNC_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const SYNC_KEY = 'happyears-last-sync';
const SYNC_STATUS_KEY = 'happyears-sync-status';

let syncIntervalId = null;
let syncCallbacks = [];
let isSyncing = false;

/**
 * Register a callback to be called when sync occurs
 * @param {Function} callback - Function to call on sync: (dataKey, success, error) => void
 */
export const onSyncCallback = (callback) => {
  syncCallbacks.push(callback);
  return () => {
    syncCallbacks = syncCallbacks.filter((cb) => cb !== callback);
  };
};

/**
 * Dispatch sync event to all listeners
 * @param {string} dataKey - What was synced
 * @param {boolean} success - Sync success status
 * @param {Error} error - Error if sync failed
 */
const dispatchSyncEvent = (dataKey, success, error = null) => {
  syncCallbacks.forEach((callback) => {
    try {
      callback(dataKey, success, error);
    } catch (e) {
      console.error('[Sync] Callback error:', e);
    }
  });

  // Dispatch custom event for components not using callbacks
  window.dispatchEvent(
    new CustomEvent('happyears-data-sync', {
      detail: { dataKey, success, error, timestamp: Date.now() },
    })
  );
};

/**
 * Record sync timestamp
 * @param {string} dataKey - What was synced
 */
export const recordSyncTimestamp = (dataKey) => {
  try {
    const syncMap = JSON.parse(localStorage.getItem(SYNC_KEY) || '{}');
    syncMap[dataKey] = Date.now();
    localStorage.setItem(SYNC_KEY, JSON.stringify(syncMap));
  } catch (e) {
    console.warn('[Sync] Failed to record sync timestamp:', e);
  }
};

/**
 * Get last sync timestamp for a data key
 * @param {string} dataKey - Unique identifier
 * @returns {number} Milliseconds since last sync, or -1 if never synced
 */
export const getLastSyncTime = (dataKey) => {
  try {
    const syncMap = JSON.parse(localStorage.getItem(SYNC_KEY) || '{}');
    return syncMap[dataKey] || -1;
  } catch (e) {
    return -1;
  }
};

/**
 * Check if data needs periodic sync (interval-based)
 * @param {string} dataKey - Unique identifier
 * @param {number} intervalMs - Interval between syncs (default 2 min)
 * @returns {boolean} true if needs sync
 */
export const needsPeriodicSync = (dataKey, intervalMs = SYNC_INTERVAL_MS) => {
  const lastSync = getLastSyncTime(dataKey);
  if (lastSync === -1) return true; // Never synced
  return Date.now() - lastSync > intervalMs;
};

/**
 * Set sync status message displayed to user
 * @param {string} message - Status message
 * @param {string} type - 'syncing' | 'success' | 'error'
 */
export const setSyncStatus = (message, type = 'syncing') => {
  try {
    const status = {
      message,
      type,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(status));
    window.dispatchEvent(
      new CustomEvent('happyears-sync-status-changed', { detail: status })
    );
  } catch (e) {
    console.warn('[Sync] Failed to set status:', e);
  }
};

/**
 * Get current sync status
 * @returns {object} { message, type, timestamp }
 */
export const getSyncStatus = () => {
  try {
    const status = JSON.parse(sessionStorage.getItem(SYNC_STATUS_KEY) || 'null');
    return status || { message: null, type: null, timestamp: null };
  } catch (e) {
    return { message: null, type: null, timestamp: null };
  }
};

/**
 * Clear sync status
 */
export const clearSyncStatus = () => {
  sessionStorage.removeItem(SYNC_STATUS_KEY);
};

/**
 * Notify user of sync result
 * @param {string} dataKey - What was synced
 * @param {boolean} success - Was it successful
 * @param {string} message - Custom message
 */
export const notifySyncResult = (dataKey, success, message = null) => {
  const displayMessage = message || (success ? `${dataKey} refreshed` : `Failed to refresh ${dataKey}`);
  setSyncStatus(displayMessage, success ? 'success' : 'error');

  // Auto-clear success message after 3 seconds
  if (success) {
    setTimeout(() => {
      clearSyncStatus();
    }, 3000);
  }
};

/**
 * Start periodic syncing
 * @param {Function} syncFn - Async function to call for sync: () => Promise<void>
 * @param {number} intervalMs - Interval between syncs (default 2 min)
 */
export const startPeriodicSync = (syncFn, intervalMs = SYNC_INTERVAL_MS) => {
  if (syncIntervalId) {
    console.warn('[Sync] Periodic sync already running');
    return;
  }

  // Sync immediately on start
  syncFn().catch((e) => console.error('[Sync] Initial sync failed:', e));

  // Then sync every interval
  syncIntervalId = setInterval(() => {
    if (!isSyncing) {
      isSyncing = true;
      syncFn()
        .catch((e) => console.error('[Sync] Periodic sync failed:', e))
        .finally(() => {
          isSyncing = false;
        });
    }
  }, intervalMs);

  console.log(`[Sync] Started periodic sync every ${intervalMs / 1000}s`);
};

/**
 * Stop periodic syncing
 */
export const stopPeriodicSync = () => {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
    console.log('[Sync] Stopped periodic sync');
  }
};

/**
 * Check if sync is currently in progress
 * @returns {boolean}
 */
export const isSyncInProgress = () => isSyncing;

/**
 * Get all sync callbacks (for testing)
 * @returns {Function[]}
 */
export const getAllSyncCallbacks = () => [...syncCallbacks];

export { SYNC_INTERVAL_MS, dispatchSyncEvent };
